import React, { useState } from "react";
import "./PeregrineBlogs.css";
import MainLayout from "../../components/MainLayout/MainLayout";
import Images from "../../constants/Images";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import PeregrineBlogCard from "../../components/PeregrineBlogCard/PeregrineBlogCard";

const weeksData = [
  {
    week: "Week 1 - Building Foundations",
    description: (
      <div>
        <p>
          In the inaugural week of the Peregrine Software Development Graduate
          Programme, I delved into fundamental skills crucial for professional
          growth. Emphasising the significance of first impressions, I dedicated
          time to honing interview techniques, refining presentation skills, and
          crafting a compelling professional brand.
        </p>
        <p>
          Furthermore, I navigated the intricacies of constructing an impactful
          and effective curriculum vitae (CV). This foundational knowledge sets
          the stage for the journey ahead, equipping me with essential tools for
          success in the dynamic realm of software development.
        </p>
      </div>
    ),
    image: Images.Week1,
  },
  {
    week: "Week 2 - Hackathon Experience and Learning",
    description: (
      <div>
        <p>
          Week 2 was an exhilarating journey filled with collaborative
          innovation and learning opportunities. I had the privilege of
          participating in Peregrine's first hackathon in London, where I
          engaged with diverse individuals from various backgrounds.
        </p>
        <p>
          Together, I embarked on a day of brainstorming sessions, tackling
          complex challenges, and leveraging our collective ingenuity to develop
          groundbreaking solutions. The atmosphere was electric, fostering a
          powerhouse of teamwork and innovation.
        </p>
        <p>
          Additionally, I seised the opportunity to deepen my technical skills
          by learning about C# programming, version control, and Software
          Development Life Cycle (SDLC). The week culminated in a presentation
          where my team showcased the software I developed during the hackathon,
          marking a rewarding end to an eventful week.
        </p>
      </div>
    ),
    image: Images.Week2,
  },
  {
    week: "Week 3 - Mastering Programming Fundamentals",
    description: (
      <div>
        <p>
          Week 3 of my Graduate Programme journey was a whirlwind of learning
          and growth. I delved deep into programming fundamentals, from
          mastering loops to exploring advanced data structures like arrays and
          dynamic lists.
        </p>
        <p>
          Putting these skills into practice, I developed a bank application,
          refining my technical abilities and problem-solving skills along the
          way. I also tackled project feasibility, which challenged me to think
          critically about project scope and resources.
        </p>
        <p>
          Outside the classroom, I continued my professional development through
          platforms like Pluralsight and O'Reilly, and found inspiration in Paul
          Vickers' book, "How to Think Like a Programmer." Each concept mastered
          and project tackled brings me one step closer to becoming a proficient
          programmer. I'm eager to see what lies ahead in the coming weeks of my
          journey.
        </p>
      </div>
    ),
    image: Images.Week3,
  },
  {
    week: "Week 4 Recap: Exceptions, Files, and Relational Patterns in C#",
    description: (
      <div>
        <p>
          Week 4 of my software development journey was packed with learning
          experiences. Here's a condensed version of what went down:
        </p>
        <p>
          Exception Handling in C#: Explored the ins and outs of handling errors
          gracefully in C#. From common exceptions like divide by zero to
          crafting custom ones, I honed my skills in ensuring code reliability.
        </p>
        <p>
          File Handling with C# File Class: Dived into the File class in C#,
          mastering techniques for reading, writing, and managing file
          operations. From text files to directories, I gained practical
          experience with file handling.
        </p>
        <p>
          Relational Patterns Exploration: Deepened my understanding of
          relational patterns in C# through a Pluralsight course. Leveraging
          these patterns fosters cleaner, more scalable code, enhancing project
          maintainability.
        </p>
        <p>
          Group Project Kickoff: Commenced a thrilling group project to develop
          a fully functional banking app. Collaborated with peers to brainstorm
          ideas, create flowcharts, UML diagrams, and meticulously document
          project requirements.
        </p>
        <p>
          Excited to share the banking app progress next week. Stay tuned for
          insights into the development journey and innovative solutions.
          Wrapping up Week 4 of my software development program, filled with
          learning and growth. Until next time, happy coding!
        </p>
      </div>
    ),
    image: Images.Week4,
  },
  {
    week: "Week 5 - Mastering SQL and Docker Compose",
    description: (
      <div>
        <p>
          Week 5 was pivotal in my Peregrine's Software Development Graduate
          Programme journey. I delved into SQL, covering joins, stored
          procedures, triggers, and normalisation techniques, crucial for
          database management.
        </p>
        <p>
          SQL, foundational in software development, was a focus this week.
          Through practical exercises, I honed my skills in data manipulation
          and querying, laying the groundwork for robust database solutions.
        </p>
        <p>
          An assessment task challenged me to create an Entity-Relationship
          Diagram (ERD) for an IT service company, reinforcing my understanding
          of database design best practices.
        </p>
        <p>
          Additionally, I explored data administration and security best
          practices in SQL, essential for ensuring data integrity and access
          control.
        </p>
        <p>
          In tandem, my group completed and submitted our banking application
          project, a testament to our collaborative effort and skills in
          software development and project management.
        </p>
        <p>
          Excited for what lies ahead, I'll continue sharing updates on my
          journey. Stay tuned for more!
        </p>
      </div>
    ),
    image: Images.Week5,
  },
  {
    week: "Week 6 - Introduction to ASP.NET Core and Unit Testing",
    description: (
      <div>
        <p>
          Week 6 marked an exciting journey into ASP.NET Core, a powerful
          framework for building web applications. I immersed myself in the
          Model-View-Controller (MVC) architecture, laying the foundation for
          creating robust and scalable web applications.
        </p>
        <p>
          With ASP.NET Core, I learned to set up the development environment
          effortlessly, allowing seamless integration with Visual Studio and
          other tools. Through practical exercises, I gained hands-on experience
          in creating a basic CRUD (Create, Read, Update, Delete) application,
          exploring controllers, actions, and routing.
        </p>
        <p>
          Additionally, I delved into the realm of unit testing using MSTest,
          NUnit, and XUnit. Unit testing plays a pivotal role in ensuring code
          quality and reliability, and I was thrilled to explore different
          testing frameworks and methodologies.
        </p>
        <p>
          One of the highlights of the week was learning about benchmarking
          applications using BenchmarkDotNet. This powerful tool allowed me to
          measure the performance of my applications accurately, enabling
          optimisation and refinement.
        </p>
        <p>
          As the Easter holiday approaches, I look forward to applying my
          newfound knowledge and skills to the tasks assigned, striving for
          continuous improvement and excellence in software development. Stay
          tuned for more updates on my journey!
        </p>
      </div>
    ),
    image: Images.Week6,
  },
  {
    week: "Week 7 - Advancing Skills Amidst Easter Break",
    description: (
      <div>
        <p>
          Week 7 brought a blend of learning opportunities and independent tasks
          as we navigated through the Easter break. We delved into SQL database
          assessments, finalised project documents, completed Microsoft Learning
          certifications, and explored design patterns.
        </p>
        <p>Key highlights included:</p>
        <ul>
          <li>
            Comprehensive SQL database assessment covering design, querying, and
            maintenance.
          </li>
          <li>
            Finalisation of Design and Requirements documents for our project.
          </li>
          <li>
            Completion of Microsoft Learning certifications in ASP.NET Core and
            Minimal APIs.
          </li>
          <li>
            Exploration of the Gang of Four design patterns, enhancing our
            architectural understanding.
          </li>
        </ul>
        <p>
          As we conclude another productive week, I'm excited about the progress
          made and look forward to applying these skills in future challenges.
        </p>
        <p>Until next time, onwards and upwards!</p>
      </div>
    ),
    image: Images.Week7,
  },
  {
    week: "Week 8 - Embracing Learning and Celebrations",
    description: (
      <div>
        <p>
          Week 8 at Peregrine was a dynamic blend of learning and celebration.
          We made significant progress in our educational journey.
        </p>
        <p>
          The week began with a focus on advancing our understanding of software
          testing principles through the ISTQB certification. Additionally, we
          dedicated time to catch up on coursework from platforms like
          Pluralsight and freeCodeCamp, as well as reading informative books on
          the O'Reilly platform and "How to Think Like a Programmer."
        </p>
        <p>
          One of the highlights was our exploration of DevOps principles, where
          we delved into methodologies like Waterfall and Agile, followed by
          hands-on experience with CI/CD using Jenkins. The practical scenario
          of creating pipelines added a new dimension to our understanding of
          DevOps practices.
        </p>
        <p>
          Continuing our technical journey, we also explored WinForms in C#,
          enhancing our proficiency in desktop application development.
        </p>
        <p>
          Amidst the week's learning activities, I took a day off on April 10th
          to celebrate Eid-Al Fitr with my family and friends, marking the end
          of Ramadan. It was a joyous occasion filled with cherished moments and
          heartfelt celebrations.
        </p>
        <p>
          Wrapping up the week on a high note, I successfully completed my
          Introduction to SQL and Design Patterns in C# courses on Pluralsight.
          These courses provided invaluable insights into database management
          and software design practices, further enhancing my skills and
          knowledge.
        </p>
        <p>
          As I look forward to the upcoming weeks, my goal is to complete the
          remaining courses and continue striving for excellence in software
          development. Stay tuned for more updates on my journey!
        </p>
        <p>Until next time, Adios! 🫡</p>
      </div>
    ),
    image: Images.Week8,
  },
  {
    week: "Week 9 - Advancing Skills and Group Projects",
    description: (
      <div>
        <p>
          Week 9 at Peregrine was another whole week of learning and
          self-improvement. We continued our journey with the presentation we
          made in week 8, swiftly finalising and submitting it. Afterward, we
          delved into exploring acronyms in programming and solidified our
          understanding of the SOLID principles.
        </p>
        <p>
          The highlight of the week came on Friday when we were tasked with a
          group assessment: to develop a full-stack Appointment Management
          System using C# and .NET MVC. The progress made by every team member
          has been exceptional, with each individual bringing their expertise to
          the table.
        </p>
        <p>
          In our team, I assumed the primary role of developing a responsive
          UI/UX for the application using Bootstrap. Additionally, I took on the
          responsibility of reviewing the code being pushed to the repository
          and providing assistance with backend tasks when needed. Alongside me,
          we had two backend developers focusing on the backend logic, CRUD
          functionality, DbContext, and Identity Roles. Our Lead Developer
          provided invaluable guidance throughout the application architecture
          and functional process, ensuring cohesion and efficiency in our
          development efforts.
        </p>
        <p>
          With the deadline for deployment approaching next week, the project is
          looking very promising. We're excited to continue working together and
          deliver a high-quality solution.
        </p>
        <p>
          Stay tuned for next week's blog, where I'll provide updates on our
          project's development and deployment. Until then, peace out!
        </p>
      </div>
    ),
    image: Images.Week9,
  },
  {
    week: "Week 10 - Achievements and Anticipation",
    description: (
      <div>
        <p>
          Week 10 at Peregrine was another intense period of collaboration,
          continued learning, and goal achievement. We kicked off the week with
          an in-depth exploration of Personal Effectiveness, Resilience, and
          Wellbeing, essential aspects of professional and personal growth.
        </p>
        <p>
          As mentioned in week 8, we were engaged in a group assessment, and I'm
          thrilled to announce that we successfully completed and submitted the
          assessment with flying colors. It was a testament to our teamwork,
          dedication, and proficiency in software development.
        </p>
        <p>
          On a personal note, I achieved the Foundational C# Certification from
          Microsoft through freeCodeCamp and completed the Design Patterns in C#
          course on Pluralsight. These accomplishments added to the excitement
          of the week, fueling my passion for continuous learning and skill
          development.
        </p>
        <p>
          With all tasks, assessments, and exercises for this program completed
          successfully, we now shift our focus to the final two weeks. I'm
          eagerly anticipating diving into the development of a Blog App using
          C#, ASP.NET, and Blazor. It's the final stretch of the program, and
          while the end is in sight, there's still much to accomplish. The
          anticipation of the upcoming challenges and opportunities fills me
          with excitement and motivation.
        </p>
        <p>
          Stay tuned for further updates as I navigate through the final weeks
          of this journey. Until then, CIAO!
        </p>
      </div>
    ),
    image: Images.Week10,
  },
  {
    week: "Week 11 - Final Project Crunch Time",
    description: (
      <div>
        <p>
          Week 11 at Peregrine was another chapter in my journey of growth and
          learning. With the final project looming on the horizon, I dove
          headfirst into the task at hand.
        </p>
        <p>
          My project, the BlogBlastApp, is shaping up nicely. It's a secure and
          intuitive blogging platform built with Razor SSR .NET 8, C#, and SQL
          Server SSMS. The features include user authentication, blog post
          management, commenting, and performance optimisation. And let me tell
          you, it's been quite the adventure.
        </p>
        <p>
          Currently, I'm knee-deep in developing the Admin Panel functionality
          for my app. It's been a challenging learning curve, with bugs popping
          up left, right, and center. But hey, that's all part of the process,
          right? I've been diving into different solutions, trying to squash
          those bugs and make the Admin Panel as smooth as possible.
        </p>
        <p>
          As I wrap up this week, I can't help but feel a sense of excitement
          and anticipation. Next week marks the final stretch, where I'll have
          completed my project and will be ready to present it to the world.
          It's been an incredible journey, and I can't wait to share the final
          result with you all.
        </p>
        <p>
          Stay tuned for next week's update, where I'll give you the lowdown on
          my completed project and the journey it took to get there. Until then,
          peace out!
        </p>
      </div>
    ),
    image: Images.Week11,
  },
  {
    week: "Week 12 - The Final Stretch and New Beginnings",
    description: (
      <div>
        <p>
          Here I am, at week 12 of the Peregrine Software Development Graduate
          Programme, and what a ride it has been! This week was a whirlwind of
          learning, excitement, and reflection as I reached the culmination of
          my journey.
        </p>
        <p>
          As mentioned in my last week's blog, I've been pouring my heart and
          soul into my final project. Well, I'm thrilled to announce that I've
          not only finished it but also submitted it with pride. It's been a
          journey filled with challenges, late nights, and moments of triumph,
          and seeing it all come together is nothing short of incredible.
        </p>
        <p>
          But wait, there's more! The icing on the cake, or should I say the
          cherry on top, is that I received a glowing recommendation letter from
          my trainer,{" "}
          <a href="https://www.linkedin.com/in/trevorsmithuk/">Trevor Smith</a>.
          It's moments like these that remind me of the value of hard work and
          dedication.
        </p>
        <p>
          Reflecting on my time in the Peregrine Graduate Programme, I feel
          truly blessed and grateful. It's been an amazing journey of growth,
          both personally and professionally. From day one, I've soaked up
          knowledge like a sponge, gaining confidence as an individual and as a
          software engineer.
        </p>
        <p>
          Now, as I look to the future, my sights are set on landing a job as a
          software engineer. I'm eager to put my newfound skills to use, working
          on real-world projects that have a tangible impact on people's lives.
          But this is just the beginning. I have dreams of one day starting my
          own IT business, and I'm determined to make it a reality.
        </p>
        <p>
          The road ahead may be long and winding, but with every step, I'll
          continue to learn, grow, and inspire others to chase their dreams.
          This journey has been a roller coaster ride of ups and downs, but I
          wouldn't have it any other way.
        </p>
        <p>
          As the curtains close on the Graduate Programme, I bid farewell for
          now. But rest assured, this isn't the end of my adventure. I'll be
          back soon with new challenges, new triumphs, and new stories to share.
          Until then, remember: You are beautiful, you are one of a kind. SMILE
          MORE!
        </p>
      </div>
    ),
    image: Images.Week12,
  },
];

const PeregrineBlogs = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  // State for expanded blog posts
  const [expandedPosts, setExpandedPosts] = useState({});

  const togglePost = (index) => {
    setExpandedPosts((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Intersection observer hooks for scroll animations
  const [headerRef, headerInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [timelineRef, timelineInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Handle redirect to Peregrine website
  const handleRedirect = () => {
    window.open("https://www.peregrineresourcing.com/", "_blank");
  };

  return (
    <MainLayout>
      <div className="peregrine-wrapper">
        {/* Header Section */}
        <section className="peregrine-header-section" ref={headerRef}>
          <div className="peregrine-container">
            <motion.div
              className="peregrine-header"
              initial={{ opacity: 0, y: 20 }}
              animate={
                headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
              }
              transition={{ duration: 0.8 }}
            >
              <motion.h1 className="peregrine-title" variants={headerVariants}>
                My Peregrine Journey
              </motion.h1>
              <div className="peregrine-title-underline"></div>
              <motion.p
                className="peregrine-subtitle"
                initial={{ opacity: 0 }}
                animate={headerInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                A 12-week software development experience at Peregrine
              </motion.p>

              <motion.div
                className="peregrine-intro"
                initial={{ opacity: 0 }}
                animate={headerInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                <p>
                  Follow my transformative journey through Peregrine's Software
                  Development Graduate Programme. Over these 12 weeks, I've
                  gained technical skills, tackled real-world projects, and
                  grown professionally. Each week brought new challenges and
                  victories, shaping me into a confident software engineer ready
                  to make an impact in the tech industry.
                </p>
                <motion.button
                  className="peregrine-cta-button"
                  onClick={handleRedirect}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Visit Peregrine Website{" "}
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="peregrine-timeline-section" ref={timelineRef}>
          <div className="peregrine-container">
            <div className="peregrine-timeline-header">
              <h2>Weekly Journey</h2>
              <p>Explore my week-by-week progress and learnings</p>
            </div>

            <motion.div
              className="peregrine-timeline"
              variants={containerVariants}
              initial="hidden"
              animate={timelineInView ? "visible" : "hidden"}
            >
              {weeksData.map((week, index) => (
                <PeregrineBlogCard
                  key={index}
                  week={week}
                  index={index}
                  expanded={expandedPosts[index] || false}
                  toggleExpand={() => togglePost(index)}
                />
              ))}
            </motion.div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default PeregrineBlogs;
